/*
 * cart-tracking-fix.js
 * ---------------------------------------------------------------------------
 * Fixes add-to-cart tracking when a bundle app (Kaching Bundles) drives the
 * add to cart on the product page.
 *
 * Problem 1 - mixed Ajax payload.
 *   Bundle apps inject items[0][id], items[0][quantity], ... into the theme's
 *   product form, but the theme's own top-level `id` and `quantity` hidden
 *   inputs are still there. Shopify's /cart/add.js rejects a payload that
 *   contains both, so the add fails silently: no cart is created, the
 *   product_added_to_cart web pixel event never fires, and nothing shows up in
 *   Analytics > Customer behavior > Active carts.
 *   Fix: strip the top-level keys whenever items[] is present.
 *
 * Problem 2 - cart permalink checkout.
 *   When the bundle app is set to skip the cart it sends the customer to
 *   /cart/<variant>:<qty>?discount=..., which builds the cart server-side at
 *   checkout. Again: no storefront cart, no add_to_cart event.
 *   Fix: intercept the permalink, do a real /cart/add.js first, then continue
 *   to checkout with the same discount.
 *
 * Everything is wrapped in try/catch. If anything is unexpected we fall back
 * to the browser's native behaviour, so this can never block a sale.
 * Set window.CART_FIX_DEBUG = true in the console to see what it is doing.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var LOG = '[cart-tracking-fix]';

  function debug() {
    if (!window.CART_FIX_DEBUG) return;
    try {
      console.log.apply(console, [LOG].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  /* ---- 1. sanitise /cart/add payloads ------------------------------- */

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  function sanitizeFormData(body) {
    var hasItems = false;

    try {
      var keys = [];
      body.forEach(function (value, key) {
        keys.push(key);
      });
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].slice(0, 6) === 'items[') {
          hasItems = true;
          break;
        }
      }
    } catch (e) {
      return;
    }

    if (!hasItems) return;
    if (!body.has('id') && !body.has('quantity')) return;

    debug('items[] detected - removing top-level id / quantity / selling_plan');
    body.delete('id');
    body.delete('quantity');
    body.delete('selling_plan');
  }

  var nativeFetch = window.fetch;

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var body = init && init.body;

        if (isCartAddUrl(url) && body && typeof FormData !== 'undefined' && body instanceof FormData) {
          sanitizeFormData(body);
        }
      } catch (e) {
        debug('sanitize skipped', e);
      }

      return nativeFetch.apply(this, arguments);
    };
  }

  /* ---- 2. turn cart permalinks into a real add to cart --------------- */

  var PERMALINK = /^\/cart\/((?:\d+:\d+)(?:,\d+:\d+)*)\/?$/;

  function parsePermalink(href) {
    if (!href) return null;

    var link;
    try {
      link = new URL(href, window.location.origin);
    } catch (e) {
      return null;
    }

    if (link.origin !== window.location.origin) return null;

    var match = link.pathname.match(PERMALINK);
    if (!match) return null;

    var items = match[1].split(',').map(function (pair) {
      var parts = pair.split(':');
      return { id: Number(parts[0]), quantity: Number(parts[1]) };
    });

    return { items: items, discount: link.searchParams.get('discount') };
  }

  function goToCheckout(discount) {
    window.location.href = discount ? '/checkout?discount=' + encodeURIComponent(discount) : '/checkout';
  }

  document.addEventListener(
    'click',
    function (event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;

      var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!anchor) return;

      var parsed = parsePermalink(anchor.getAttribute('href'));
      if (!parsed) return;

      event.preventDefault();
      debug('intercepting cart permalink', parsed);

      nativeFetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: parsed.items })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (data && data.status) debug('add to cart returned an error', data);
          goToCheckout(parsed.discount);
        })
        .catch(function (error) {
          debug('add to cart failed, falling back to the permalink', error);
          window.location.href = anchor.href;
        });
    },
    true
  );

  debug('loaded');
})();
